import { useMemo } from 'react'

import { type TeamMemberDto, TeamMemberRole } from '@api'
import type { ColumnDef } from '@tanstack/react-table'
import { useUnit } from 'effector-react'

import { DataTable } from '@/components/ui/table/data-table.tsx'
import { Tag } from '@/components/ui/tag.tsx'
import { $account } from '@/models/account.ts'
import { TeamActivityProvider } from '@/modules/team/hooks/use-team-activity-states.tsx'
import { TeamAvatar } from '@/modules/team/ui/team-avatar.tsx'

import { LiveTeamActivityState } from '../ui/members/list/live-team-activity-state'
import { TeamMemberActions } from '../ui/members/list/team-actions'
import { TeamMemberRoleChanger } from '../ui/members/team-role-changer'

const getTeamTableColumns = (memberRole?: TeamMemberRole) => {
  const columns: ColumnDef<TeamMemberDto>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            <div className="relative">
              <TeamAvatar
                name={row.original.name}
                avatarSrc={row.original.profilePicture}
                className="h-12 w-12"
              />
              <LiveTeamActivityState id={row.original.userId} />
            </div>
            <span className="truncate">{row.original.name}</span>
          </div>
        )
      },
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: 'E-Mail Address',
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => <TeamMemberRoleChanger row={row} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status')

        return (
          <Tag
            withDot
            variant={status === 'Active' ? 'success' : 'neutral'}
          >
            {row.original.status}
          </Tag>
        )
      },
    },
    {
      id: 'actions',
      header: () => <span className="float-right">Actions</span>,
      cell: ({ row }) => <TeamMemberActions row={row} />,
    },
  ]

  // Hide the "Email" and "Actions" columns for members
  if (memberRole === TeamMemberRole.MEMBER) {
    const emailColumnIndex = columns.findIndex(
      (column) => column.id === 'email',
    )

    columns.splice(emailColumnIndex, 1)

    const actionsColumnIndex = columns.findIndex(
      (column) => column.id === 'actions',
    )

    columns.splice(actionsColumnIndex, 1)
  }

  return columns
}

interface Props {
  searchQuery: string
  data: TeamMemberDto[]
}

export const TeamTable = ({ data, searchQuery }: Props) => {
  const account = useUnit($account)

  const columns = getTeamTableColumns(account?.memberRole)

  const teamMembers = useMemo(
    () =>
      (data ?? []).filter((member) =>
        member.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [data, searchQuery],
  )

  return (
    <TeamActivityProvider>
      <DataTable
        testId="team"
        enableSorting
        columns={columns}
        data={teamMembers}
      />
    </TeamActivityProvider>
  )
}
